import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import debounce from "lodash.debounce";
import api from "config/axiosConfig";
import InputField from "ui/InputField";
import EntityAvatar from "components/Entities/EntityAvatar";
import { logger } from "lib/logger";
import { apiRoutes } from "util/apiRoutes";
import { useUser } from "util/UserContext";
import { useBodyScrollLock } from "ui/useBodyScrollLock";

interface Props {
    onSelect: (suggestion: ActorSuggestion) => void;
    onEntityChipClose?: () => void;
    onSearchChange?: () => void;
    placeholder?: string;
    excludeIds?: number[];
    includeIds?: number[];
    actorTypes: ("user" | "team")[];
    inputType?: "search" | "add";
    className?: string;
    autoFocus?: boolean;
    fieldRequired?: boolean;
    validationMessage?: string;
    validationState?: "none" | "valid" | "invalid";
    fieldValue?: string;
    prepopulateData?: ActorSuggestion | null;
    /**
     * When false (default), the authenticated user is filtered out of the
     * suggestion list. Nearly every caller (coauthor pickers, team invites,
     * mention search) is choosing *another* actor, so making the current user
     * opt-in prevents accidental self-selection. Set to `true` only when a
     * flow legitimately lets a user pick themselves (e.g. self-assignment).
     */
    includeSelf?: boolean;
}

export type ActorSuggestion = {
    id: number;
    type: "user" | "team";
    name: string;
    display_name?: string;
    username?: string;
    teamname?: string;
    avatar_key?: string;
    properties_public?: any;
};

const ActorSearch: React.FC<Props> = ({
    onSelect,
    onSearchChange,
    placeholder = "Search users or teams...",
    excludeIds = [],
    includeIds = [],
    actorTypes,
    inputType = "search",
    className = "",
    autoFocus = false,
    fieldRequired = false,
    fieldValue = "",
    validationMessage = "",
    validationState = "none",
    onEntityChipClose,
    prepopulateData = null,
    includeSelf = false,
}) => {
    const { currentUser } = useUser();

    /**
     * Combine caller-provided excludes with the current user id when needed.
     * Centralizing this here keeps callers from needing to wire in
     * `currentUser?.id` themselves and guarantees the server also sees the
     * exclusion (so paginated results can't sneak the self row back in).
     */
    const effectiveExcludeIds = useMemo<number[]>(() => {
        const base = Array.isArray(excludeIds) ? excludeIds : [];
        if (includeSelf) {
            return base;
        }
        const selfId = currentUser?.id;
        if (typeof selfId !== "number" || Number.isNaN(selfId)) {
            return base;
        }
        if (base.includes(selfId)) {
            return base;
        }
        return [...base, selfId];
    }, [excludeIds, includeSelf, currentUser?.id]);
    const [searchQuery, setSearchQuery] = useState(fieldValue || "");
    const [searchSuggestions, setSearchSuggestions] = useState<ActorSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [actorSelected, setActorSelected] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState<ActorSuggestion | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;
    useBodyScrollLock(showSuggestions);

    useEffect(() => {
        if (prepopulateData) {
            setSearchQuery(prepopulateData.name);
            setActorSelected(true);
            setSelectedSuggestion(prepopulateData);
        } else {
            setActorSelected(false);
            setSelectedSuggestion(null);
            setSearchQuery("");
        }
    }, [prepopulateData]);

    const performSearch = async (value: string) => {
        try {
            setSearchError(null);
            setIsSearching(true);
            let suggestions: ActorSuggestion[] = [];

            const response = await api.get(apiRoutes.actors.root, {
                params: {
                    q: value,
                    actorType: actorTypes.join(","),
                    limit: 15,
                    offset: 0,
                    ...(effectiveExcludeIds.length > 0 ? { excludeActorId: effectiveExcludeIds.join(",") } : {}),
                    ...(includeIds.length > 0 ? { includeActorId: includeIds.join(",") } : {}),
                },
            });
            suggestions = response.data.map((actor: any) => ({
                id: parseInt(actor.id, 10),
                type: actor.actor_type,
                name: actor.actor_name,
                display_name: actor.actor_display_name,
                avatar_key: actor.properties_public?.avatar_key,
                properties_public: actor.properties_public
            }));

            setSearchSuggestions(suggestions);
            setShowSuggestions(true);
        } catch (error) {
            setSearchSuggestions([]);
            setShowSuggestions(true);
            setSearchError("Unable to load search results. Please try again.");
            logger.warn("Actor search failed", { error, query: value });
        } finally {
            setIsSearching(false);
        }
    };

    // Debounce the search to prevent excessive API calls
    //eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((value: string) => performSearch(value), 360),
        [effectiveExcludeIds, includeIds, actorTypes]
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        setShowSuggestions(true);
        debouncedSearch(value);
        if (onSearchChange) {
            onSearchChange();
        }
    };

    const handleClearSearch = () => {
        setSearchQuery("");
        setSearchSuggestions([]);
        setShowSuggestions(false);
        searchInputRef.current?.blur();
    };

    const handleSelectSuggestion = (suggestion: ActorSuggestion) => {
        onSelect(suggestion);
        setSelectedSuggestion(suggestion);
        setShowSuggestions(false);
        setSearchQuery("");
        setActorSelected(false);
    };

    const handleEntityChipClose = () => {
        setSelectedSuggestion(null);
        setActorSelected(false);
        setSearchQuery("");
        if (onEntityChipClose) {
            onEntityChipClose();
        }
        document.getElementById(`actor-search-${placeholder}`)?.focus();
    };

    return (
        <div className={`relative ${className}`}>
            <div className="relative">
                {inputType === "search" ? (
                    <>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            ref={searchInputRef}
                            placeholder={placeholder}
                            autoFocus={autoFocus}
                            className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
                        />
                        {searchQuery.length > 0 && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {actorSelected ? (
                            <span className={`input-container relative flex-1`}>
                                <div
                                    className={`
                                        w-full min-h-[60px] px-3 pt-6 pb-1
                                        eoe-input bg-gray-200 dark:bg-eoe-dark-input
                                        rounded-2xl
                                        text-gray-800 dark:text-eoe-dark-text
                                        transition-colors
                                        resize-none
                                    `}
                                >
                                    <span 
                                        className={`
                                            absolute left-3
                                            top-2 text-xs
                                            transition-all
                                            text-eoe-text-meta dark:text-eoe-dark-text-meta
                                        `}
                                    >
                                        {placeholder}
                                        {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                                    </span>
                                    <EntityAvatar
                                        entity={selectedSuggestion as any}
                                        entityType={selectedSuggestion?.type || "user"}
                                        entityChip={true}
                                        showCloseChip={true}
                                        onEntityChipClose={handleEntityChipClose}
                                    />
                                </div>
                            </span>
                        ) : (
                            <InputField
                                id={`actor-search-${placeholder}`}
                                type="text"
                                name={`actor-search-${placeholder}`}
                                labelText={placeholder}
                                fieldRequired={fieldRequired}
                                value={searchQuery || fieldValue}
                                onChange={handleSearchChange}
                                onFocus={() => performSearch("")}
                                autoComplete="off"
                                ref={searchInputRef}
                                autoFocus={prepopulateData ? true : autoFocus}
                                validationState={validationState}
                                error={validationMessage}
                            />
                        )}
                    </>
                )}
            </div>

            {showSuggestions && (
                <div
                    className={`search-suggestions-container flex flex-col bg-transparent 
                        ${inputType === "search" ?
                            "fixed top-[56px] left-[1px] right-[1px] z-50 max-h-[calc(100dvh-72px)] overflow-hidden md:w-[580px] md:mx-auto md:left-0 md:right-0" : 
                            "relative bottom-2 top-1 max-h-[200px] overflow-y-auto"
                        }`
                    }
                >
                    <ul
                        className={`bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 shadow-lg
                            ${inputType === "search" ? 
                                "flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]" :
                                "flex-1 overflow-y-auto"
                            }
                        `}
                    >
                        {searchSuggestions.map((suggestion, index) => (
                            <li
                                key={index}
                                className="
                                    group
                                    text-eoe-text dark:text-eoe-dark-text
                                "
                            >
                                <button
                                    type="button"
                                    className="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon"
                                    onClick={() => handleSelectSuggestion(suggestion)}
                                >
                                    <div className="flex items-center space-x-2">
                                        <span className="w-8 h-8">
                                            <img 
                                                src={suggestion.avatar_key ? `${IMAGE_CDN}/${suggestion.avatar_key}` : "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"} 
                                                alt={suggestion.name} 
                                                className="w-full h-full rounded-full object-cover" 
                                            />
                                        </span>
                                        <div className="flex flex-col">
                                            <span>
                                                {suggestion.name}
                                            </span>
                                            <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                                {suggestion.name}
                                            </span>
                                        </div>
                                    </div>
                                    <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                                        suggestion.type === "team" ? "bg-eoe-entity-team" : "bg-eoe-entity-user"
                                    } text-white`}>
                                        {suggestion.type === "team" ? "Partea" : "User"}
                                    </span>
                                </button>
                            </li>
                        ))}
                        {isSearching && (
                            <li className="px-3 py-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                Searching...
                            </li>
                        )}
                        {!isSearching && searchError && (
                            <li className="px-3 py-2 text-sm text-warning-red dark:text-warning-red-dark">
                                {searchError}
                            </li>
                        )}
                        {!isSearching && !searchError && searchSuggestions.length === 0 && (
                            <li className="px-3 py-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                No results found.
                            </li>
                        )}
                    </ul>
                    <div className="flex-shrink-0 border-none">
                        <button 
                            className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none" 
                            onClick={() => setShowSuggestions(false)}
                        >
                            <span className="text-eoe-dark-text">Close search</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActorSearch; 